<template>
  <div class="landing">
    <!-- NAV -->
    <header class="nav">
      <div class="nav__inner">
        <div class="brand">
          <div class="brand__mark">
            <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 34s-12-7.4-12-16a7 7 0 0 1 12-4.9A7 7 0 0 1 32 18c0 8.6-12 16-12 16z"
                    fill="url(#kmg)"/>
              <defs>
                <linearGradient id="kmg" x1="8" y1="8" x2="32" y2="34" gradientUnits="userSpaceOnUse">
                  <stop stop-color="#ff4d8d"/>
                  <stop offset="1" stop-color="#a020f0"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
          <span class="brand__name">KissMora</span>
        </div>
        <nav class="nav__links">
          <a href="#features">Features</a>
          <a href="#how">How it works</a>
          <a href="#stories">Stories</a>
        </nav>
        <div class="nav__cta">
          <button class="btn btn--ghost" @click="goLogin">Login</button>
          <button class="btn btn--primary" @click="goRegister">Join Free</button>
        </div>
      </div>
    </header>

    <!-- HERO -->
    <section class="hero">
      <div class="hero__bg">
        <div class="blob blob--1"></div>
        <div class="blob blob--2"></div>
        <div class="blob blob--3"></div>
      </div>
      <div class="hero__inner">
        <div class="hero__copy">
          <span class="pill">✨ Find your spark</span>
          <h1 class="hero__title">
            Find your spark.<br/>
            Meet the one who<br/>
            <span class="gradient-text">kisses back.</span>
          </h1>
          <p class="hero__sub">
            KissMora is where meaningful connections bloom. Meet singles near you,
            chat safely, and let real chemistry take the lead.
          </p>
          <div class="hero__cta">
            <button class="btn btn--primary btn--xl" @click="goRegister">
              Start your story — It's Free
            </button>
            <button class="btn btn--ghost btn--xl" @click="goLogin">I already have an account</button>
          </div>
          <div class="hero__trust">
            <div class="avatars">
              <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&h=120&fit=crop&crop=faces" alt=""/>
              <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=120&h=120&fit=crop&crop=faces" alt=""/>
              <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&h=120&fit=crop&crop=faces" alt=""/>
              <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=120&h=120&fit=crop&crop=faces" alt=""/>
            </div>
            <div class="trust__text">
              <strong>250,000+</strong> singles already making connections
              <div class="stars">★★★★★ <span>4.9 / 5</span></div>
            </div>
          </div>
        </div>
        <div class="hero__art">
          <div class="poster-frame">
            <img src="/brand/poster.png" alt="KissMora — Find your spark"/>
          </div>
          <div class="float-card float-card--1">
            <div class="fc__emoji">💌</div>
            <div>
              <div class="fc__title">New message</div>
              <div class="fc__sub">"Coffee tomorrow? ☕"</div>
            </div>
          </div>
          <div class="float-card float-card--2">
            <div class="fc__emoji">✨</div>
            <div>
              <div class="fc__title">12 new likes</div>
              <div class="fc__sub">Someone's interested!</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FEATURES -->
    <section class="features" id="features">
      <div class="section__head">
        <span class="eyebrow">Why KissMora</span>
        <h2>Dating, reimagined for real humans.</h2>
      </div>
      <div class="features__grid">
        <div class="feature">
          <div class="feature__icon" style="background:linear-gradient(135deg,#ff6b9d,#ff4d8d)">🔐</div>
          <h3>Verified Profiles</h3>
          <p>Phone &amp; photo verification on every account. No bots, no catfish — just real people.</p>
        </div>
        <div class="feature">
          <div class="feature__icon" style="background:linear-gradient(135deg,#a020f0,#7209b7)">🎯</div>
          <h3>Smart Matching</h3>
          <p>Our algorithm learns what you love and finds people who vibe with you — not just swipe-bait.</p>
        </div>
        <div class="feature">
          <div class="feature__icon" style="background:linear-gradient(135deg,#ff4d8d,#a020f0)">💬</div>
          <h3>Safe, Private Chat</h3>
          <p>End-to-end encrypted conversations. Your number stays yours until you choose to share.</p>
        </div>
        <div class="feature">
          <div class="feature__icon" style="background:linear-gradient(135deg,#f72585,#b5179e)">🌍</div>
          <h3>Worldwide Connections</h3>
          <p>From your neighborhood to the other side of the world — love has no postcode on KissMora.</p>
        </div>
        <div class="feature">
          <div class="feature__icon" style="background:linear-gradient(135deg,#7209b7,#560bad)">🎁</div>
          <h3>Virtual Gifts</h3>
          <p>Send roses, kisses &amp; surprises. Break the ice without awkward silences.</p>
        </div>
        <div class="feature">
          <div class="feature__icon" style="background:linear-gradient(135deg,#ff4d8d,#ff6b9d)">🏆</div>
          <h3>Daily Rewards</h3>
          <p>Log in daily, spin the wheel, earn boosts. Good vibes rewarded every day.</p>
        </div>
      </div>
    </section>

    <!-- HOW IT WORKS -->
    <section class="how" id="how">
      <div class="section__head">
        <span class="eyebrow">3 steps to love</span>
        <h2>Simple. Honest. Yours.</h2>
      </div>
      <div class="how__steps">
        <div class="step">
          <div class="step__num">01</div>
          <div class="step__img">
            <img src="https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?w=600&h=600&fit=crop" alt=""/>
          </div>
          <h3>Create your vibe</h3>
          <p>Upload your best photos, share what makes you, you. Takes under 60 seconds.</p>
        </div>
        <div class="step">
          <div class="step__num">02</div>
          <div class="step__img">
            <img src="https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=600&h=600&fit=crop" alt=""/>
          </div>
          <h3>Discover matches</h3>
          <p>Swipe, like, and meet people who actually match your wavelength.</p>
        </div>
        <div class="step">
          <div class="step__num">03</div>
          <div class="step__img">
            <img src="https://images.unsplash.com/photo-1516589091380-5d8e87df6999?w=600&h=600&fit=crop" alt=""/>
          </div>
          <h3>Start the spark</h3>
          <p>Chat, flirt, and take it from the app to the real world — when you're ready.</p>
        </div>
      </div>
    </section>

    <!-- STORIES -->
    <section class="stories" id="stories">
      <div class="section__head">
        <span class="eyebrow">Love stories</span>
        <h2>Kisses that started here.</h2>
      </div>
      <div class="stories__grid">
        <div class="story">
          <img src="https://images.unsplash.com/photo-1522098635833-216c03d81fbe?w=800&h=500&fit=crop" alt=""/>
          <div class="story__body">
            <p>"We matched on a Tuesday, had coffee on Friday, and moved in together six months later. KissMora just gets it."</p>
            <div class="story__who">— Maya &amp; Daniel, Paris</div>
          </div>
        </div>
        <div class="story">
          <img src="https://images.unsplash.com/photo-1518621736915-f3b1c41bfd00?w=800&h=500&fit=crop" alt=""/>
          <div class="story__body">
            <p>"I stopped believing in apps — then KissMora showed me someone two blocks away. We're getting married this fall."</p>
            <div class="story__who">— Priya &amp; Leo, Mumbai</div>
          </div>
        </div>
        <div class="story">
          <img src="https://images.unsplash.com/photo-1529636798458-92182e662485?w=800&h=500&fit=crop" alt=""/>
          <div class="story__body">
            <p>"The verified profiles made it feel safe. The smart matching made it feel magical."</p>
            <div class="story__who">— Aiko &amp; Ryan, Tokyo</div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="cta">
      <div class="cta__inner">
        <h2>Your person is out there.<br/><span class="gradient-text">Let's find them.</span></h2>
        <p>Join 250,000+ singles who found something real.</p>
        <button class="btn btn--primary btn--xl" @click="goRegister">Create your free account</button>
      </div>
    </section>

    <!-- FOOTER -->
    <footer class="footer">
      <div class="footer__inner">
        <div class="brand">
          <div class="brand__mark">
            <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 34s-12-7.4-12-16a7 7 0 0 1 12-4.9A7 7 0 0 1 32 18c0 8.6-12 16-12 16z" fill="url(#kmg2)"/>
              <defs>
                <linearGradient id="kmg2" x1="8" y1="8" x2="32" y2="34" gradientUnits="userSpaceOnUse">
                  <stop stop-color="#ff4d8d"/>
                  <stop offset="1" stop-color="#a020f0"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
          <span class="brand__name">KissMora</span>
        </div>
        <div class="footer__cols">
          <div>
            <h4>Product</h4>
            <a href="#features">Features</a>
            <a href="#how">How it works</a>
            <a href="#stories">Stories</a>
          </div>
          <div>
            <h4>Company</h4>
            <a href="#">About</a>
            <a href="#">Careers</a>
            <a href="#">Press</a>
          </div>
          <div>
            <h4>Support</h4>
            <a href="#">Help Center</a>
            <a href="#">Safety Tips</a>
            <a href="#">Community Rules</a>
          </div>
          <div>
            <h4>Legal</h4>
            <a href="#">Privacy</a>
            <a href="#">Terms</a>
            <a href="#">Cookies</a>
          </div>
        </div>
      </div>
      <div class="footer__bar">
        © {{ year }} KissMora. Made with 💖 for people who believe in real.
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'Landing',
  data(){
    return { year: new Date().getFullYear() }
  },
  mounted(){
    // if already logged in, go to app
    if(localStorage.getItem('token')){
      this.$router.replace('/Home');
    }
  },
  methods: {
    goLogin(){ this.$router.push('/Login'); },
    goRegister(){ this.$router.push('/Register'); }
  }
}
</script>

<style scoped>
/* ====== RESET / BASE ====== */
.landing {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, "PingFang SC", "Hiragino Sans GB", sans-serif;
  color: #1a1033;
  background: #fff9fb;
  overflow-x: hidden;
  min-height: 100vh;
  line-height: 1.5;
}
.landing * { box-sizing: border-box; }
.landing h1, .landing h2, .landing h3, .landing h4 { margin: 0; font-weight: 800; letter-spacing: -0.02em; }
.landing p { margin: 0; }

.gradient-text {
  background: linear-gradient(90deg, #ff4d8d 0%, #a020f0 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

/* ====== BUTTONS ====== */
.btn {
  display: inline-flex; align-items: center; justify-content: center;
  font-family: inherit; font-weight: 700; cursor: pointer;
  border-radius: 999px; border: none; outline: none;
  padding: 14px 28px; font-size: 15px;
  transition: transform .15s ease, box-shadow .2s ease, background .2s ease;
}
.btn:active { transform: translateY(1px); }
.btn--primary {
  background: linear-gradient(90deg, #ff4d8d 0%, #a020f0 100%);
  color: #fff;
  box-shadow: 0 8px 24px rgba(255,77,141,.35);
}
.btn--primary:hover { box-shadow: 0 12px 32px rgba(255,77,141,.5); transform: translateY(-2px); }
.btn--ghost {
  background: rgba(255,255,255,.6);
  color: #1a1033;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(26,16,51,.12);
}
.btn--ghost:hover { background: #fff; }
.btn--xl { padding: 18px 36px; font-size: 17px; }

/* ====== NAV ====== */
.nav {
  position: sticky; top: 0; z-index: 50;
  background: rgba(255,249,251,.8); backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(26,16,51,.06);
}
.nav__inner {
  max-width: 1200px; margin: 0 auto; padding: 16px 24px;
  display: flex; align-items: center; justify-content: space-between; gap: 24px;
}
.brand { display: flex; align-items: center; gap: 10px; }
.brand__mark { width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; }
.brand__mark svg { width: 100%; height: 100%; }
.brand__name {
  font-size: 22px; font-weight: 800;
  background: linear-gradient(90deg, #ff4d8d 0%, #a020f0 100%);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
}
.nav__links { display: flex; gap: 32px; }
.nav__links a {
  color: #1a1033; text-decoration: none; font-weight: 600; font-size: 15px;
  opacity: .8; transition: opacity .2s;
}
.nav__links a:hover { opacity: 1; }
.nav__cta { display: flex; gap: 10px; }

/* ====== HERO ====== */
.hero { position: relative; overflow: hidden; padding: 40px 0 80px; }
.hero__bg { position: absolute; inset: 0; z-index: 0; overflow: hidden; }
.blob {
  position: absolute; border-radius: 50%; filter: blur(80px); opacity: .5;
  animation: float 14s ease-in-out infinite;
}
.blob--1 { width: 500px; height: 500px; background: #ff4d8d; top: -100px; left: -100px; }
.blob--2 { width: 400px; height: 400px; background: #a020f0; top: 150px; right: -80px; animation-delay: -5s; }
.blob--3 { width: 350px; height: 350px; background: #ffb3d1; bottom: -50px; left: 30%; animation-delay: -9s; }
@keyframes float {
  0%, 100% { transform: translate(0,0) scale(1); }
  33% { transform: translate(40px,-30px) scale(1.05); }
  66% { transform: translate(-30px,40px) scale(.95); }
}
.hero__inner {
  position: relative; z-index: 1;
  max-width: 1200px; margin: 0 auto; padding: 0 24px;
  display: grid; grid-template-columns: 1.1fr 1fr; gap: 48px; align-items: center;
}
.pill {
  display: inline-block; padding: 8px 18px; border-radius: 999px;
  background: rgba(255,255,255,.7); border: 1px solid rgba(255,77,141,.25);
  font-size: 14px; font-weight: 600; color: #a020f0; margin-bottom: 24px;
  backdrop-filter: blur(8px);
}
.hero__title { font-size: 64px; line-height: 1.05; margin-bottom: 24px; }
.hero__sub { font-size: 19px; color: #4a3d66; max-width: 520px; margin-bottom: 32px; }
.hero__cta { display: flex; gap: 14px; flex-wrap: wrap; margin-bottom: 40px; }
.hero__trust { display: flex; align-items: center; gap: 18px; }
.avatars { display: flex; }
.avatars img {
  width: 44px; height: 44px; border-radius: 50%; border: 3px solid #fff9fb;
  margin-left: -12px; object-fit: cover;
}
.avatars img:first-child { margin-left: 0; }
.trust__text { font-size: 14px; color: #4a3d66; }
.trust__text strong { color: #1a1033; }
.stars { color: #ffc107; font-size: 13px; margin-top: 2px; }
.stars span { color: #4a3d66; margin-left: 6px; }

.hero__art { position: relative; }
.poster-frame {
  width: min(100%, 480px); aspect-ratio: 1; margin: 0 auto;
  background: #000; border-radius: 28px; overflow: hidden; position: relative;
  box-shadow: 0 30px 80px rgba(160,32,240,.35), 0 0 0 8px rgba(255,255,255,.45);
}
.poster-frame img { width: 100%; height: 100%; object-fit: cover; display: block; }
.phone__overlay {
  position: absolute; inset: 0;
  background: linear-gradient(180deg, transparent 50%, rgba(26,16,51,.7) 100%);
  display: flex; align-items: flex-end; justify-content: center; padding: 24px;
}
.match-card {
  background: rgba(255,255,255,.95); border-radius: 20px; padding: 20px;
  text-align: center; width: 100%; backdrop-filter: blur(20px);
  box-shadow: 0 20px 50px rgba(0,0,0,.2);
}
.match-card__heart { font-size: 42px; margin-bottom: 6px; animation: beat 1.5s ease-in-out infinite; }
.match-card__title {
  font-size: 22px; font-weight: 800; margin-bottom: 4px;
  background: linear-gradient(90deg, #ff4d8d, #a020f0);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
}
.match-card__sub { font-size: 13px; color: #4a3d66; }
@keyframes beat { 0%,100% { transform: scale(1); } 50% { transform: scale(1.15); } }

.float-card {
  position: absolute; background: #fff; border-radius: 16px; padding: 14px 18px;
  box-shadow: 0 10px 30px rgba(26,16,51,.15);
  display: flex; align-items: center; gap: 12px;
  animation: bounce 4s ease-in-out infinite;
}
.float-card--1 { top: 28px; left: -16px; }
.float-card--2 { bottom: 32px; right: -16px; animation-delay: -2s; }
.fc__emoji { font-size: 24px; }
.fc__title { font-size: 13px; font-weight: 700; }
.fc__sub { font-size: 12px; color: #4a3d66; }
@keyframes bounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }

/* ====== SECTIONS ====== */
.section__head { text-align: center; max-width: 700px; margin: 0 auto 56px; padding: 0 24px; }
.eyebrow {
  display: inline-block; font-size: 13px; font-weight: 700; letter-spacing: .15em;
  text-transform: uppercase; color: #ff4d8d; margin-bottom: 14px;
}
.section__head h2 { font-size: 44px; line-height: 1.1; }

/* FEATURES */
.features { padding: 100px 0; background: #fff; }
.features__grid {
  max-width: 1200px; margin: 0 auto; padding: 0 24px;
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px;
}
.feature {
  padding: 32px; background: #fff9fb; border-radius: 24px;
  border: 1px solid rgba(26,16,51,.06);
  transition: transform .25s, box-shadow .25s;
}
.feature:hover { transform: translateY(-4px); box-shadow: 0 20px 40px rgba(160,32,240,.1); }
.feature__icon {
  width: 56px; height: 56px; border-radius: 16px;
  display: flex; align-items: center; justify-content: center;
  font-size: 28px; margin-bottom: 20px;
  box-shadow: 0 8px 20px rgba(255,77,141,.25);
}
.feature h3 { font-size: 20px; margin-bottom: 8px; }
.feature p { color: #4a3d66; font-size: 15px; }

/* HOW */
.how { padding: 100px 0; background: linear-gradient(180deg, #fff9fb 0%, #ffe8f0 100%); }
.how__steps {
  max-width: 1100px; margin: 0 auto; padding: 0 24px;
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px;
}
.step { text-align: center; }
.step__num {
  font-size: 14px; font-weight: 800; color: #ff4d8d; letter-spacing: .1em; margin-bottom: 16px;
}
.step__img {
  width: 180px; height: 180px; margin: 0 auto 24px;
  border-radius: 50%; overflow: hidden; position: relative;
  box-shadow: 0 20px 50px rgba(160,32,240,.25);
  border: 6px solid #fff;
}
.step__img img { width: 100%; height: 100%; object-fit: cover; }
.step h3 { font-size: 22px; margin-bottom: 10px; }
.step p { color: #4a3d66; font-size: 15px; max-width: 280px; margin: 0 auto; }

/* STORIES */
.stories { padding: 100px 0; background: #fff; }
.stories__grid {
  max-width: 1200px; margin: 0 auto; padding: 0 24px;
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px;
}
.story {
  border-radius: 24px; overflow: hidden; background: #fff9fb;
  border: 1px solid rgba(26,16,51,.06);
  transition: transform .25s;
}
.story:hover { transform: translateY(-4px); }
.story img { width: 100%; height: 220px; object-fit: cover; display: block; }
.story__body { padding: 24px; }
.story__body p { font-size: 15px; color: #1a1033; margin-bottom: 14px; font-style: italic; }
.story__who { font-size: 13px; font-weight: 700; color: #ff4d8d; }

/* CTA */
.cta {
  padding: 100px 24px;
  background: linear-gradient(135deg, #1a1033 0%, #4a1a6e 100%);
  position: relative; overflow: hidden;
}
.cta::before {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(circle at 20% 50%, rgba(255,77,141,.3), transparent 50%),
              radial-gradient(circle at 80% 50%, rgba(160,32,240,.3), transparent 50%);
}
.cta__inner { position: relative; max-width: 800px; margin: 0 auto; text-align: center; color: #fff; }
.cta__inner h2 { font-size: 52px; line-height: 1.1; margin-bottom: 16px; }
.cta__inner p { font-size: 19px; opacity: .8; margin-bottom: 36px; }

/* FOOTER */
.footer { background: #0f0820; color: #d4c5e8; padding: 60px 24px 30px; }
.footer__inner {
  max-width: 1200px; margin: 0 auto;
  display: grid; grid-template-columns: 1fr 2fr; gap: 48px; padding-bottom: 40px;
  border-bottom: 1px solid rgba(255,255,255,.1);
}
.footer .brand__name { font-size: 24px; }
.footer__cols { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; }
.footer__cols h4 { color: #fff; font-size: 14px; margin-bottom: 14px; letter-spacing: .05em; text-transform: uppercase; }
.footer__cols a { display: block; color: #d4c5e8; text-decoration: none; font-size: 14px; margin-bottom: 10px; opacity: .7; }
.footer__cols a:hover { opacity: 1; color: #ff4d8d; }
.footer__bar { max-width: 1200px; margin: 24px auto 0; text-align: center; font-size: 13px; opacity: .6; }

/* ====== RESPONSIVE — TABLET ====== */
@media (max-width: 960px) {
  .nav__links { display: none; }
  .nav__cta .btn--ghost { display: none; }
  .nav__inner { padding: 14px 20px; }

  .hero { padding: 24px 0 64px; }
  .hero__inner { grid-template-columns: 1fr; text-align: center; gap: 32px; padding: 0 20px; }
  .hero__title { font-size: 48px; }
  .hero__sub { margin-left: auto; margin-right: auto; font-size: 17px; }
  .hero__cta { justify-content: center; flex-direction: column; align-items: center; }
  .hero__cta .btn { width: 100%; max-width: 320px; }
  .hero__trust { justify-content: center; flex-wrap: wrap; }
  .hero__art { margin-top: 20px; }
  .float-card { display: flex; }

  .section__head { margin-bottom: 44px; }
  .section__head h2 { font-size: 34px; }

  .features, .how, .stories { padding: 80px 0; }
  .features__grid { grid-template-columns: repeat(2, 1fr); gap: 18px; padding: 0 20px; }
  .feature { padding: 26px; }
  .how__steps { grid-template-columns: 1fr; gap: 40px; padding: 0 20px; }
  .stories__grid { grid-template-columns: 1fr; gap: 18px; padding: 0 20px; max-width: 560px; }
  .cta__inner h2 { font-size: 38px; }
  .footer__inner { grid-template-columns: 1fr; gap: 36px; padding-bottom: 32px; }
  .footer__cols { grid-template-columns: repeat(2, 1fr); }
}

/* ====== RESPONSIVE — MOBILE ====== */
@media (max-width: 520px) {
  /* Nav — compact with only the primary CTA visible */
  .nav__inner { padding: 12px 16px; gap: 12px; }
  .brand__mark { width: 30px; height: 30px; }
  .brand__name { font-size: 18px; }
  .nav__cta .btn { padding: 10px 18px; font-size: 13px; }

  /* Hero — big, centered, breathing room */
  .hero {
    padding: 32px 0 72px;
    background: radial-gradient(circle at 50% 0%, rgba(255,77,141,.08), transparent 60%);
  }
  .blob { filter: blur(60px); opacity: .4; }
  .blob--1 { width: 320px; height: 320px; top: -80px; left: -100px; }
  .blob--2 { width: 260px; height: 260px; top: 100px; right: -80px; }
  .blob--3 { width: 220px; height: 220px; bottom: -40px; left: 20%; }
  .hero__inner { padding: 0 18px; gap: 28px; }
  .pill { font-size: 12px; padding: 7px 14px; margin-bottom: 18px; }
  .hero__title { font-size: 38px; line-height: 1.08; margin-bottom: 18px; letter-spacing: -.025em; }
  .hero__sub { font-size: 15px; margin-bottom: 24px; padding: 0 6px; }
  .hero__cta { margin-bottom: 28px; gap: 10px; }
  .hero__cta .btn--xl { padding: 15px 22px; font-size: 14px; }
  .hero__trust { flex-direction: column; gap: 10px; }
  .avatars img { width: 38px; height: 38px; border-width: 2px; margin-left: -10px; }

  /* Poster — square wordmark, no phone crop */
  .poster-frame {
    width: min(86vw, 340px); border-radius: 22px;
    box-shadow: 0 20px 50px rgba(160,32,240,.3), 0 0 0 6px rgba(255,255,255,.4);
  }

  /* Float cards — keep them on mobile but smaller */
  .float-card { padding: 10px 14px; border-radius: 14px; }
  .float-card--1 { top: 12px; left: 0; }
  .float-card--2 { bottom: 12px; right: 0; }
  .fc__emoji { font-size: 20px; }
  .fc__title { font-size: 12px; }
  .fc__sub { font-size: 10px; }

  /* Sections — tighter vertical rhythm */
  .features, .how, .stories { padding: 64px 0; }
  .section__head { margin-bottom: 36px; padding: 0 20px; }
  .section__head h2 { font-size: 28px; line-height: 1.15; }
  .eyebrow { font-size: 11px; margin-bottom: 10px; }

  /* Feature cards — single column, compact */
  .features__grid { grid-template-columns: 1fr; gap: 14px; padding: 0 18px; }
  .feature { padding: 22px; border-radius: 20px; }
  .feature__icon { width: 48px; height: 48px; border-radius: 14px; font-size: 24px; margin-bottom: 14px; }
  .feature h3 { font-size: 18px; }
  .feature p { font-size: 14px; }

  /* How steps */
  .how__steps { gap: 32px; padding: 0 20px; }
  .step__img { width: 140px; height: 140px; border-width: 5px; margin-bottom: 18px; }
  .step h3 { font-size: 19px; }
  .step p { font-size: 14px; }

  /* Stories */
  .stories__grid { padding: 0 18px; }
  .story { border-radius: 20px; }
  .story img { height: 180px; }
  .story__body { padding: 20px; }
  .story__body p { font-size: 14px; }

  /* Final CTA */
  .cta { padding: 72px 20px; }
  .cta__inner h2 { font-size: 30px; line-height: 1.15; }
  .cta__inner p { font-size: 15px; margin-bottom: 26px; }
  .btn--xl { padding: 15px 26px; font-size: 15px; }

  /* Footer */
  .footer { padding: 48px 20px 24px; }
  .footer__inner { gap: 28px; padding-bottom: 28px; }
  .footer__cols { grid-template-columns: repeat(2, 1fr); gap: 20px; }
  .footer__cols h4 { font-size: 13px; margin-bottom: 10px; }
  .footer__cols a { font-size: 13px; margin-bottom: 8px; }
  .footer__bar { font-size: 12px; margin-top: 20px; }
}

@media (max-width: 360px) {
  .hero__title { font-size: 32px; }
  .section__head h2 { font-size: 24px; }
  .cta__inner h2 { font-size: 26px; }
  .poster-frame { width: min(88vw, 280px); }
  .nav__cta .btn { padding: 9px 14px; font-size: 12px; }
  .brand__name { font-size: 17px; }
}
</style>
